import React, {
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  Navbar,
  Container,
  Button,
  Dropdown,
  Badge,
} from 'react-bootstrap';

import Select from 'react-select';

import {
  useLocation,
  useNavigate,
  NavLink,
} from 'react-router-dom';

import {
  useSelector,
  useDispatch,
} from 'react-redux';

import {
  serverLogout,
  logout,
  setCompanyID,
} from '../../features/auth/authSlice';

import {
  fetchActionCounts,
  resetActionCounts,
} from '../../features/actionCounts/actionCountsSlice';

import {
  FaSignOutAlt,
  FaCog,
  FaBars,
  FaBell,
  FaEnvelope,
  FaUser,
  FaArrowRight,
} from 'react-icons/fa';

import useCompanyContext from '../../hooks/useCompanyContext';
import {
  isCompanyContextLockedRoute,
} from '../../utils/companyContextRoutes';

import logo from '../../assets/images/RMB_V-HORIZONTAL_VERDE.png';
import collapsedLogo from '../../assets/images/RMB_IMAGOTIPO.png';

function Topbar({
  toggleSidebar,
  collapsed,
}) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const {
    user,
    allCompanies,
    isAuthenticated,
  } = useSelector(
    (state) => state.auth,
  );

  const {
    bookingPendingCount,
    requestNeedReplyCount,
  } = useSelector(
    (state) => state.actionCounts,
  );

  const {
    isAdmin,
    currentCompanyID,
    buildCompanyUrl,
  } = useCompanyContext();

  const isCompanyContextLocked =
    isCompanyContextLockedRoute(
      location.pathname,
    );

  const [isMobile, setIsMobile] = useState(false);

  /**
   * Topbar badge 数量。
   */
  const bookingsCount = bookingPendingCount;
  const requestsCount = requestNeedReplyCount;

  /**
   * 第一次进入后台时获取 Topbar 数量。
   *
   * 注意：
   * 不依赖 companyID。
   *
   * 后端规则：
   * ADMIN => 永远全局
   * SUPPLIER => 永远当前 token supplier
   */
  useEffect(() => {
    if (!user || !isAuthenticated) {
      return;
    }

    dispatch(fetchActionCounts());
  }, [
    dispatch,
    user,
    isAuthenticated,
  ]);

  /**
   * 每 60 秒刷新一次 Topbar 数量。
   * fetchActionCounts 内部如果有缓存/防抖，会继续生效。
   */
  useEffect(() => {
    if (!user || !isAuthenticated) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      dispatch(fetchActionCounts());
    }, 60000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [
    dispatch,
    user,
    isAuthenticated,
  ]);

  /**
   * 检测手机 / 小屏幕。
   */
  useEffect(() => {
    const compute = () => window.innerWidth <= 1200;

    const setIfChanged = () => {
      setIsMobile((prev) => {
        const next = compute();
        return prev === next ? prev : next;
      });
    };

    setIfChanged();

    let timer;

    const onResize = () => {
      clearTimeout(timer);
      timer = setTimeout(setIfChanged, 100);
    };

    window.addEventListener('resize', onResize);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', onResize);
    };
  }, []);

  /**
   * ADMIN company selector options。
   * 0 = All companies。
   */
  const companyOptions = useMemo(
    () => [
      {
        value: 0,
        label: 'All companies',
      },
      ...(allCompanies || [])
        .map((company) => {
          const companyID =
            company.id ??
            company.companyID ??
            company.companyId;

          if (!companyID) {
            console.warn(
              'Invalid company option:',
              company,
            );

            return null;
          }

          return {
            value: String(companyID),
            label:
              company.companyName ||
              company.name ||
              `Company ${companyID}`,
          };
        })
        .filter(Boolean),
    ],
    [allCompanies],
  );

  /**
   * 当前 company selector value。
   */
  const selectedCompanyOption = useMemo(() => {
    if (!isAdmin) {
      return null;
    }

    return (
      companyOptions.find(
        (option) =>
          String(option.value) ===
          String(currentCompanyID),
      ) ||
      companyOptions[0] ||
      null
    );
  }, [
    isAdmin,
    companyOptions,
    currentCompanyID,
  ]);

  /**
   * ADMIN 切换 company。
   *
   * company context locked route 上禁止切换，
   * 避免当前页面的数据上下文和 URL 不一致。
   */
  const handleCompanyChange = (option) => {
    if (
      !isAdmin ||
      !option ||
      isCompanyContextLocked
    ) {
      return;
    }

    const nextCompanyID =
      option.value === 0 ||
        option.value === '0'
        ? 0
        : String(option.value);

    dispatch(
      setCompanyID(nextCompanyID),
    );

    const currentTarget =
      `${location.pathname}${location.search}${location.hash}`;

    navigate(
      buildCompanyUrl(
        currentTarget,
        nextCompanyID,
      ),
      {
        replace: true,
      },
    );
  };

  const handleLogout = async () => {
    try {
      await dispatch(
        serverLogout(),
      ).unwrap();
    } catch (error) {
      console.warn(
        'Server logout failed:',
        error?.message,
      );
    }

    dispatch(
      resetActionCounts(),
    );

    dispatch(
      logout(),
    );

    navigate(
      '/login',
      {
        replace: true,
        state: {
          from: 'logout',
          timestamp: Date.now(),
        },
      },
    );
  };

  return (
    <Navbar className="shadow-sm topbar">
      <Container
        fluid
        className="d-flex justify-content-between ps-0"
      >
        <div className="d-flex align-items-center">
          {/* Logo */}
          <NavLink
            to="/dashboard"
            className={
              `me-md-3 me-sm-3 d-flex align-items-center fixCorner custom-nav-link ${collapsed
                ? 'collapsed-logo p-0'
                : 'collapse-logo'
              }`
            }
          >
            <img
              src={
                collapsed
                  ? collapsedLogo
                  : logo
              }
              alt="Logo"
              className="RMB-logo"
            />
          </NavLink>

          {/* Sidebar */}
          <Button
            variant="light"
            onClick={toggleSidebar}
            className="border-0 me-2 topbar-toggle"
          >
            {
              isMobile
                ? (
                  <FaBars size={25} />
                )
                : collapsed
                  ? (
                    <FaArrowRight size={25} />
                  )
                  : (
                    <FaBars size={25} />
                  )
            }
          </Button>

          {/* ADMIN Supplier selector */}
          {isAdmin && (
            <div style={{ width: 300 }}>
              <Select
                value={selectedCompanyOption}
                onChange={handleCompanyChange}
                options={companyOptions}
                isSearchable={!isCompanyContextLocked}
                isClearable={false}
                isDisabled={isCompanyContextLocked}
                placeholder="Select company"
                noOptionsMessage={() => 'No companies found'}
              />
            </div>
          )}
        </div>

        {/* Right side */}
        <div className="d-flex align-items-center">
          {/* Pending bookings */}
          <NavLink
            to="/bookings/list"
            className="position-relative me-4 topbar-toggle"
          >
            <FaEnvelope size={25} />

            {bookingsCount > 0 && (
              <Badge
                pill
                bg="danger"
                className="position-absolute envelope-number"
                style={{
                  top: '-5px',
                  right: '-10px',
                  transform: 'scale(0.8)',
                }}
              >
                {bookingsCount > 99
                  ? '99+'
                  : bookingsCount}
              </Badge>
            )}
          </NavLink>

          {/* Requests need reply */}
          <NavLink
            to="/requests/list"
            className="position-relative me-3 topbar-toggle"
          >
            <FaBell size={25} />

            {requestsCount > 0 && (
              <Badge
                pill
                bg="danger"
                className="position-absolute envelope-number"
                style={{
                  top: '-5px',
                  right: '-10px',
                  transform: 'scale(0.8)',
                }}
              >
                {requestsCount > 99
                  ? '99+'
                  : requestsCount}
              </Badge>
            )}
          </NavLink>

          {/* User */}
          <Dropdown align="end">
            <Dropdown.Toggle
              variant="light"
              className="border-0 topbar-toggle"
            >
              <FaUser size={25} />
            </Dropdown.Toggle>

            <Dropdown.Menu
              align="end"
              className="user-dropdown-menu p-2"
            >
              <div className="user-info d-flex justify-content-between align-items-center px-3">
                <div>
                  <strong>
                    {user?.firstName}{' '}
                    {user?.lastName}
                  </strong>

                  <div>
                    {user?.role === 'ADMIN'
                      ? 'Administrator'
                      : ''}
                  </div>
                </div>
              </div>

              <Dropdown.Divider />

              <Dropdown.Item
                as={NavLink}
                to="/settings"
                className="d-flex align-items-center"
              >
                <FaCog className="me-2" />
                Setting
              </Dropdown.Item>

              <Dropdown.Item
                onClick={handleLogout}
                className="d-flex align-items-center"
              >
                <FaSignOutAlt className="me-2" />
                Log Out
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </Container>
    </Navbar>
  );
}

export default Topbar;
